import { create } from 'zustand';

export const useFriendsStore = create((set, get) => ({
  friends: [],
  pendingRequests: [],
  isLoading: false,
  error: null,
  showAll: false,
  isOwnProfile: false,
  outgoingRequest: false,
  areFriends: false,
  totalFriends: 0,

  // Fetch friends for a profile
  fetchFriends: async (profileId, accessToken, showAll = false) => {
    set({ isLoading: true, error: null });

    try {
      const url = `/api/friends/profile/${profileId}${showAll ? '?all=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch friends');
      }

      set({
        friends: data.friends || [],
        pendingRequests: data.pending_requests || [],
        isOwnProfile: data.is_own_profile,
        outgoingRequest: data.outgoing_request,
        areFriends: data.are_friends,
        totalFriends: data.total_friends,
        showAll: showAll,
        isLoading: false
      });

      return { success: true };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message
      });
      return { success: false, error: error.message };
    }
  },

  // Send friend request
  sendFriendRequest: async (addresseeProfileId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch('/api/friends/request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ addressee_profile_id: addresseeProfileId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send friend request');
      }

      // Update outgoing request status
      set({ outgoingRequest: true });

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Accept friend request
  acceptFriendRequest: async (friendshipId, accessToken, profileId) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/friends/request/${friendshipId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to accept friend request');
      }

      // Refresh friends list
      await get().fetchFriends(profileId, accessToken, get().showAll);

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Reject friend request
  rejectFriendRequest: async (friendshipId, accessToken, profileId) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/friends/request/${friendshipId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reject friend request');
      }

      // Refresh friends list
      await get().fetchFriends(profileId, accessToken, get().showAll);

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Unfriend
  unfriend: async (friendProfileId, accessToken, profileId) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/friends/profile/${friendProfileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to unfriend');
      }

      // Refresh friends list
      await get().fetchFriends(profileId, accessToken, get().showAll);

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Toggle show all friends
  toggleShowAll: async (profileId, accessToken) => {
    const newShowAll = !get().showAll;
    await get().fetchFriends(profileId, accessToken, newShowAll);
  },

  // Clear friends data
  clearFriends: () => set({
    friends: [],
    pendingRequests: [],
    isLoading: false,
    error: null,
    showAll: false,
    isOwnProfile: false,
    outgoingRequest: false,
    areFriends: false,
    totalFriends: 0
  })
}));
